import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { AuthService } from '@core/auth/auth.service';
import { UserService } from 'app/core/user/user.service';
import { NavigationService } from '@core/navigation/navigation.service';
import { HeaderTextService } from '@shared/components/header-text/header-text.service';
import { environment } from 'environments/environment';
import { Subject, combineLatest } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

const EMBED_DEBUG = true;

@Component({
    selector: 'app-jedstion-embed',
    standalone: true,
    imports: [CommonModule],
    styles: [
        `
            :host {
                display: block;
                height: 100%;
                min-height: 0;
            }

            .embed-shell {
                width: 100%;
                height: 100%;
                min-height: 0;
                background: #fff;
            }

            .embed-iframe {
                width: 100%;
                height: 100%;
                border: 0;
                display: block;
            }
        `,
    ],
    template: `
        <div class="embed-shell">
            <ng-container *ngIf="safeUrl; else configErrorTpl">
                <iframe
                    class="embed-iframe"
                    [src]="safeUrl"
                    loading="lazy"
                    scrolling="yes"
                    referrerpolicy="strict-origin-when-cross-origin"
                ></iframe>
            </ng-container>

            <ng-template #configErrorTpl>
                <div class="w-full h-full flex items-center justify-center p-6">
                    <div class="max-w-xl text-center">
                        <h2 class="text-xl font-semibold text-slate-900">Integracion Jedstion no configurada</h2>
                        <p class="mt-2 text-sm text-slate-600">
                            Configura <code>environment.jedstion.embedBaseUrl</code> en este proyecto para habilitar el iframe.
                        </p>
                    </div>
                </div>
            </ng-template>
        </div>
    `,
})
export class JedstionEmbedComponent implements OnInit, OnDestroy {
    safeUrl: SafeResourceUrl | null = null;
    private _destroy$ = new Subject<void>();

    constructor(
        private _route: ActivatedRoute,
        private _router: Router,
        private _sanitizer: DomSanitizer,
        private _authService: AuthService,
        private _userService: UserService,
        private _navigationService: NavigationService,
        private _headerTextService: HeaderTextService
    ) {}

    ngOnInit(): void {
        if (EMBED_DEBUG) {
            console.log('[EMBED-DEBUG][EmbedComponent] ngOnInit', {
                url: this._router.url,
                query: { ...this._route.snapshot.queryParams },
            });
        }

        combineLatest([this._route.paramMap, this._route.queryParamMap])
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => this.buildEmbedUrl());

        if (typeof window !== 'undefined') {
            window.addEventListener('message', this.handleIframeMessage);
        }
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
        if (typeof window !== 'undefined') {
            window.removeEventListener('message', this.handleIframeMessage);
        }
    }

    private handleIframeMessage = (event: MessageEvent): void => {
        const baseUrl = (environment as any)?.jedstion?.embedBaseUrl || '';
        if (baseUrl) {
            try {
                const originUrl = new URL(event.origin);
                const targetUrl = new URL(baseUrl);
                if (originUrl.origin !== targetUrl.origin) {
                    return;
                }
            } catch (e) {
                // Error de parseo de origen
            }
        }

        if (event.data && event.data.type === 'IFRAME_READY') {
            const sourceWindow = event.source as Window;
            if (sourceWindow) {
                const routeQueryParams: Params = { ...this._route.snapshot.queryParams };
                const queryToken = (routeQueryParams.token || '').toString().trim();
                const configuredToken = ((environment as any)?.jedstion?.embedToken || '').trim();
                const sessionToken = (this._authService.accessToken || '').trim();
                const tokenToForward = queryToken || configuredToken || sessionToken;
                const user = this._userService.user;

                if (tokenToForward) {
                    const messagePayload = {
                        type: 'SET_TOKEN',
                        token: tokenToForward,
                        user: user ? {
                            nombre: user.nombre || user.name || '',
                            apellido: user.apellido || '',
                            email: user.email,
                            nick: user.nick
                        } : null
                    };
                    sourceWindow.postMessage(messagePayload, '*');
                }
            }
        }
    };

    private buildEmbedUrl(): void {
        const moduloRaw = (this._route.snapshot.paramMap.get('modulo') || '').trim();
        const submoduloRaw = (this._route.snapshot.paramMap.get('submodulo') || '').trim();
        const idRaw = (this._route.snapshot.paramMap.get('id') || '').trim();
        const routeQueryParams: Params = { ...this._route.snapshot.queryParams };

        const moduloParsed = this.extractInlineQueryFromSegment(moduloRaw);
        const submoduloParsed = this.extractInlineQueryFromSegment(submoduloRaw);
        const idParsed = this.extractInlineQueryFromSegment(idRaw);

        Object.assign(routeQueryParams, moduloParsed.query, submoduloParsed.query, idParsed.query);

        let modulo = moduloParsed.value.toLowerCase();
        let submodulo = submoduloParsed.value.toLowerCase();
        let id = idParsed.value;
        let ruta = (routeQueryParams.ruta || '').toString().trim();

        const rutaParsed = this.extractInlineQueryFromSegment(ruta);
        ruta = rutaParsed.value;
        Object.assign(routeQueryParams, rutaParsed.query);

        if (EMBED_DEBUG) {
            console.log('[EMBED-DEBUG][EmbedComponent] build start', {
                routerUrl: this._router.url,
                moduloRaw,
                submoduloRaw,
                idRaw,
                modulo,
                submodulo,
                id,
                ruta,
                queryParams: routeQueryParams,
            });
        }

        if (
            Object.keys(moduloParsed.query).length ||
            Object.keys(submoduloParsed.query).length ||
            Object.keys(idParsed.query).length ||
            Object.keys(rutaParsed.query).length
        ) {
            const commands = ['/embed'];
            if (modulo) {
                commands.push(modulo);
            }
            if (submodulo) {
                commands.push(submodulo);
            }
            if (id) {
                commands.push(id);
            }

            this._router.navigate(commands, {
                queryParams: routeQueryParams,
                replaceUrl: true,
            });

            if (EMBED_DEBUG) {
                console.log('[EMBED-DEBUG][EmbedComponent] inline query detected -> normalizing route', {
                    commands,
                    queryParams: routeQueryParams,
                });
            }

            this.safeUrl = null;
            return;
        }

        const baseUrl = (environment as any)?.jedstion?.embedBaseUrl || '';
        const useEmbedGateway = Boolean((environment as any)?.jedstion?.useEmbedGateway);
        this.updateHeaderTitle(modulo, submodulo, id, ruta);

        if (!baseUrl || (!ruta && !modulo)) {
            this.safeUrl = null;
            return;
        }

        const queryToken = (routeQueryParams.token || '').toString().trim();
        const sendTokenInUrl = Boolean((environment as any)?.jedstion?.sendTokenInUrl);

        if (queryToken && !sendTokenInUrl) {
            const cleanParams = { ...routeQueryParams };
            delete cleanParams.token;

            const commands = ['/embed'];
            if (modulo) {
                commands.push(modulo);
            }
            if (submodulo) {
                commands.push(submodulo);
            }
            if (id) {
                commands.push(id);
            }

            this._router.navigate(commands, {
                queryParams: cleanParams,
                replaceUrl: true,
            });

            this.safeUrl = null;
            return;
        }

        const query = { ...routeQueryParams };
        delete query.layout;
        delete query.ruta;

        const configuredToken = ((environment as any)?.jedstion?.embedToken || '').trim();
        const sessionToken = (this._authService.accessToken || '').trim();
        const tokenToForward = queryToken || configuredToken || sessionToken;

        if (tokenToForward && sendTokenInUrl) {
            query.token = tokenToForward;
        }

        const queryString = Object.keys(query)
            .filter((key) => query[key] !== undefined && query[key] !== null && query[key] !== '')
            .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(String(query[key]))}`)
            .join('&');

        const cleanBaseUrl = String(baseUrl).replace(/\/+$/, '');
        const querySuffix = queryString ? `&${queryString}` : '';
        let iframeUrl = '';

        if (useEmbedGateway) {
            const idSegment = id ? `/${encodeURIComponent(id)}` : '';
            iframeUrl = `${cleanBaseUrl}/#/embed/${encodeURIComponent(modulo)}${idSegment}`;
            iframeUrl += iframeUrl.includes('?') ? querySuffix : querySuffix.replace('&', '?');
        } else {
            const pathSegments = ruta
                ? ruta.split('/').filter((segment) => segment && segment.trim() !== '')
                : [modulo];

            if (!ruta && submodulo) {
                pathSegments.push(submodulo);
            }
            if (!ruta && id) {
                pathSegments.push(id);
            }

            const appPath = pathSegments.map((segment) => encodeURIComponent(segment)).join('/');
            iframeUrl = `${cleanBaseUrl}/#/${appPath}?layout=empty&embed=1${querySuffix}`;
        }

        if (EMBED_DEBUG) {
            console.log('[EMBED-DEBUG][EmbedComponent] iframe ready', {
                baseUrl: cleanBaseUrl,
                useEmbedGateway,
                queryTokenLength: queryToken.length,
                configuredTokenLength: configuredToken.length,
                sessionTokenLength: sessionToken.length,
                tokenToForwardLength: tokenToForward.length,
                tokenPrefix: tokenToForward ? tokenToForward.slice(0, 12) : '',
                iframeUrl,
            });
        }

        this.safeUrl = this._sanitizer.bypassSecurityTrustResourceUrl(iframeUrl);
    }

    private extractInlineQueryFromSegment(segment: string): { value: string; query: Record<string, string> } {
        const rawSegment = (segment || '').trim();
        if (!rawSegment) {
            return { value: '', query: {} };
        }

        let decodedSegment = rawSegment;
        try {
            decodedSegment = decodeURIComponent(rawSegment);
        } catch {
            decodedSegment = rawSegment;
        }

        if (!decodedSegment.includes('?')) {
            return { value: decodedSegment, query: {} };
        }

        const [valuePart, ...queryParts] = decodedSegment.split('?');
        const queryString = queryParts.join('?');
        const params = new URLSearchParams(queryString);
        const query: Record<string, string> = {};

        params.forEach((value, key) => {
            query[key] = value;
        });

        return {
            value: valuePart.trim(),
            query,
        };
    }

    private updateHeaderTitle(modulo: string, submodulo: string, id: string, ruta: string): void {
        const candidates: string[] = [];
        const cleanRuta = (ruta || '').replace(/^\/+/, '').split('?')[0];

        if (cleanRuta) {
            candidates.push(`/embed/${cleanRuta}`);
        } else {
            if (modulo && submodulo && id) {
                candidates.push(`/embed/${modulo}/${submodulo}/${id}`);
            }
            if (modulo && submodulo) {
                candidates.push(`/embed/${modulo}/${submodulo}`);
            }
            if (modulo) {
                candidates.push(`/embed/${modulo}`);
            }
        }

        let menuTitle = '';
        for (const candidate of candidates) {
            const item = this._navigationService.getItemByRoute(candidate);
            if (item?.title) {
                menuTitle = item.title;
                break;
            }
        }

        if (!menuTitle) {
            const fallbackSource = cleanRuta || [modulo, submodulo].filter(Boolean).join('/');
            const fallbackSegment = fallbackSource.split('/').filter(Boolean).pop() || '';
            menuTitle = this.toHumanTitle(fallbackSegment);
        }

        if (menuTitle) {
            this._headerTextService.setTituloManual(menuTitle);
        }
    }

    private toHumanTitle(value: string): string {
        return (value || '')
            .replace(/[-_]+/g, ' ')
            .split(' ')
            .filter(Boolean)
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    }
}

